<template>
  <master-layout id="contentResourceSessions" no-bottom-pad>
    <div
      v-if="!isPageLoading && !filterPending"
      class="lg:flex-1 lg:overflow-hidden relative font-aktiv-grotesk lg:pb-[142px] bg-[#F8F9F9]"
    >
      <ResourcesContainer
        ref="resourceContainer"
        :show-more-resources="moreResourcesCount > 0"
      >
        <div
          class="md:ml-[24px] lg:ml-0 md:flex md:justify-between px-6 md:px-0"
        >
          <div
            class="md:ml-0 border-[2px] border-grey-40 text-[#637083] rounded-xl h-[48px] w-full md:w-[400px] flex flex-row items-center py-[10px] px-3 md:mx-6 cursor-pointer hover:border-black"
            @click="this.$router.push('/resources/sessions/search')"
          >
            <img
              class="mr-2 size-6"
              src="/assets/icons/search.svg"
              alt=""
            />
            <p>Search</p>
          </div>
        </div>
        <div class="font-aktiv-grotesk">
          <ResourcesSection />
        </div>
      </ResourcesContainer>
    </div>
    <div
      v-if="isPageLoading || filterPending"
      class="w-screen h-screen flex items-center justify-center"
    >
      <loading-state />
    </div>
  </master-layout>
</template>

<script>
import ResourcesContainer from "../../components/ResourcesPage/ResourcesContainer.vue";
import LoadingState from "@/components/common/LoadingState.vue";
import { mapState } from "vuex";
import { FirebaseAnalytics } from "@/utils/firebase-analytics-wrapper";
import ResourcesSection from "@/components/ResourcesPage/PlaybackResources/ResourcesSection.vue";

export default {
  name: "SessionsPage",
  components: {
    ResourcesContainer,
    LoadingState,
    ResourcesSection,
  },
  data() {
    return {
      hasInitiallyLoaded: false,
      isPageLoading: true,
    };
  },
  computed: {
    ...mapState("cms", ["sessionMaterials", "resources"]),
    ...mapState("player", ["playingPlayer"]),
    ...mapState("filter", [
      "sessions",
      "showFilterModal",
      "allResources",
      "filterType",
      "sessionIdScheduleMapping",
      "showFilterResults"
    ]),
    // A session filter is pending (in the URL) but hasn't been applied yet. While true we keep
    // the spinner up so the unfiltered "all resources" list can never paint before the filter —
    // declarative, so it holds regardless of Ionic/Vue lifecycle timing (first paint, re-entry).
    filterPending() {
      return !!this.$route.query.sessions && !this.showFilterResults;
    },
  },
  async ionViewWillEnter() {
    console.log("filter/fetchSchedule")
    // Keep the loading state up when a session filter is present (even on re-entry)
    // so the filtered results are ready before anything renders — no flash of all resources.
    if (!this.hasInitiallyLoaded || this.$route.query.sessions) {
      this.isPageLoading = true;
    }
    await this.$store.dispatch("filter/clearState");
    // use Promise.all to fetch all the data at once
    await Promise.all([
      this.$store.dispatch("filter/fetchSchedule", {
        locale: "en",
        conferenceType: "Global",
      }),
      this.$store.dispatch("filter/fetchAllResources", {
        locale: "en",
        conferenceType: "Global",
      }),
      this.$store.dispatch("filter/fetchSearchSuggestions", {
        locale: "en",
        conferenceType: "Global",
      }),
      this.$store.dispatch("filter/updateFilteredValues", {
        filteredSessions: [],
        filteredTopics: [],
      }),
      this.$store.dispatch("filter/fetchBookMarkLists", {
        locale: "en",
        conferenceType: "Global",
      }),
      this.$store.dispatch("filter/fetchWatchLists", {
        locale: "en",
        conferenceType: "Global",
      })
    ]);
    // Apply the session filter (from ?sessions=) BEFORE revealing the page, so users go
    // straight from the loading state to the filtered results — no flash of all resources.
    // (Removed a second filter/clearState() that used to run here — it wiped the
    // just-fetched resource pool, which both caused the flash and made the filter unreliable.)
    await this.queryStringToSelectFilter();
    this.$nextTick(async() => {
      this.hasInitiallyLoaded = true;
      this.isPageLoading = false;
    });
    FirebaseAnalytics.setScreenName({
      screenName: "resources",
      nameOverride: "ResourcesPage",
    });
  },
  async ionViewDidLeave() {
    const filterType = this.filterType;
    const showFilterModal = false;
    await this.$store.dispatch("filter/updateShowFilterModal", {
      showFilterModal,
      filterType,
    });
    await this.$store.dispatch("filter/clearState");
    await this.resetFilter()
  },
  methods: {
    openFilterModal() {
      this.$store.dispatch("filter/updateShowFilterModal", true);
    },
    async queryStringToSelectFilter() {
      const queryParams = this.$route.query.sessions
      if (!queryParams) return;
      const queryParamsArray = queryParams.split(',');
      const sessionCmsIds = [];
      for (const sessionIdStr of queryParamsArray) {
        try {
          const sessionId = parseInt(sessionIdStr)
          const sessionCmsId = this.sessionIdScheduleMapping[sessionId].id
          sessionCmsIds.push(sessionCmsId)
        } catch (e) {
          // noop
        }
      }

      await this.$store.dispatch("filter/updateFilteredValues", {
        filteredSessions: sessionCmsIds,
        filteredTopics: [],
      });
      await this.$store.dispatch("filter/updateShowFilterModal", {
        showFilterModal: false,
        filterType: 'Sessions',
      });
      await this.$store.dispatch("filter/updateFilteredMaterials");
      await this.$store.dispatch("filter/updateShowFilterResults", true);
    },
    async resetFilter() {
      await this.$store.dispatch("filter/updateFilteredValues", {
        filteredSessions: [],
        filteredTopics: [],
      });
      await this.$store.dispatch("filter/updateShowFilterResults", false);
    },
  },
};
</script>

<style scoped>
.tool-shadow {
  box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
}

.shadow {
  box-shadow: 0px 1px 25px 0px rgba(0, 0, 0, 0.25);
}

.swiper-slide {
  max-width: 328px;
  align-items: flex-start;
  vertical-align: middle;
  border-radius: 10px;
  display: flex;
  -webkit-transition: all 0.4s ease;
  transition: all 0.4s ease;
}

.swiper {
  list-style: none;
  z-index: 1;
  padding-bottom: 0px;
}

.download-button svg {
  fill: #00aae4;
}

.download-button:active svg {
  fill: #ffffff;
}

@media all and (min-width: 768px) {
  ::v-deep(.swiper-wrapper) {
    padding-left: 0;
  }
}
</style>
